import dynamic from "next/dynamic";
import React from "react";

// Above-fold components - Load immediately
const Banner = dynamic(() => import("./Banner"), { ssr: true });
const Trusted = dynamic(() => import("./Trusted"), { ssr: true });

// Below-fold components - Defer loading for better FCP
const Service = dynamic(() => import("./Service"), {
  ssr: true,
  loading: () => <div className="min-h-[600px] bg-gradient-to-r from-blue-50 to-blue-100 animate-pulse" />
});
const CaseStudies = dynamic(() => import("./CaseStudies"), {
  ssr: true,
  loading: () => <div className="min-h-[800px] bg-gray-50 animate-pulse" />
});
const Insights = dynamic(() => import("./Insights"), {
  ssr: true,
  loading: () => <div className="min-h-[600px] bg-gray-50 animate-pulse" />
});
const Achievements = dynamic(() => import("./Achievements"), {
  ssr: true,
  loading: () => <div className="min-h-[700px] bg-gray-50 animate-pulse" />
});
const Awards = dynamic(() => import("./Awards"), {
  ssr: true,
  loading: () => <div className="min-h-[500px] bg-gray-50 animate-pulse" />
});
const Faq = dynamic(() => import("./Faq"), {
  ssr: true,
  loading: () => <div className="min-h-[400px] bg-gray-50 animate-pulse" />
});
const RFPForm = dynamic(() => import("./Quote-home"), {
  ssr: false,
  loading: () => <div className="min-h-[600px] bg-gray-50 animate-pulse" />
});
const HocHead = dynamic(() => import("../../HigherOrderComponent/HocHead"), {
  ssr: true,
});

// Type definitions
export interface Link {
  id: number;
  href: string;
  label: string;
  target: string | null;
  isExternal: boolean;
  hover: string | null;
  action: string;
  image: string | null;
}
export interface BannerCard {
  id: number;
  title: string;
  description: string;
  varient: string;
  link: Link | null;
}
export interface BannerBlock {
  __component: "blocks.2025-home-banner";
  id: number;
  title: string;
  sub_title: string;
  description: string;
  Cta?: Link;
  banner_animation: any;
  banner_animation_link?: string;
  bannerCard: BannerCard[];
}
export interface LogoImage {
  id: number;
  name: string;
  url: string;
  width: number;
  height: number;
  mime: string;
}
export interface Logo {
  id: number;
  title: string;
  image: LogoImage;
}
export interface TrustedByBlock {
  __component: "blocks.2025-home-trusted-by";
  id: number;
  title: string;
  logos: Logo[];
}
export interface trustedCard {
  logoSrc: string;
  company: string;
}
export interface ServiceBlock {
  __component: "blocks.2025-home-eterprise";
  id: number;
  title: string;
  slide: SlideItem[];
}
export interface CaseStudiesBlock {
  __component: "blocks.2025-home-case-studies";
  id: number;
  title: string;
  sub_title: string;
  CaseCard: CaseStudiesItem[];
}
export interface SlideImage {
  id: number;
  name: string;
  url: string;
}
export interface SlideLink {
  id: number;
  href: string;
  label: string;
  target: string | null;
}
export interface SlideItem {
  id: number;
  tag: string;
  title: string;
  description: string;
  image: SlideImage;
  link: SlideLink;
  onButtonClick?: () => void | string;
  thumbDescription?: { url: string };
}
export interface TrustedByBlock {
  __component: "blocks.2025-home-trusted-by";
  id: number;
  title: string;
  slide: SlideItem[];
}
export interface CaseCard {
  id: number;
  title: string;
  Description: string;
  image: {
    url: string;
    // add other fields if needed
  };
  link: {
    href: string;
    label: string;
  };
}
export interface CaseStudiesItem {
  id: number;
  CaseStudy: CaseCard[];
}
export interface CaseStudiesItemGreen {
  id: number;
  CaseStudy: CaseCard[];
  CaseStudyGreen: CaseStudiesItem[];
  title: string;
  description: string;
  image: { url: string; label: string };
  link: SlideLink;
}
export interface InsightsBlock {
  __component: "blocks.2025-home-insights";
  id: number;
  title: string;
  sub_title: string;
  description: string;
  link?: { href: string; label: string };
}
export interface AchievementsItem {
  id: number;
  scale_up: string;
  description: string;
  image: any;
}
export interface AchievementsBlock {
  __component: "blocks.2025-home-achievements";
  id: number;
  title: string;
  image: any;
  animation_link: any;
  sub_title: string;
  description: string;
  achievementCard: AchievementsItem[];
}
export interface AwardsItem {
  id: number;
  name: string;
  logo: any;
}
export interface AwardsBlock {
  __component: "blocks.2025-home-awards";
  id: number;
  title: string;
  sub_title: string;
  awardsLogo: AwardsItem[];
}
export interface FaqItem {
  id: number;
  question: string;
  answer: {
    type: string;
    children: {
      text: string;
      type: string;
    }[];
  }[];
}
export interface FaqBlock {
  __component: "blocks.2025-home-rfp";
  id: number;
  title: string;
  homeFaqs: FaqItem[];
}
export interface RFPFormBlock {
  __component: "blocks.2025-home-rfp";
  id: number;
  title: string;
  sub_title: string;
  schedule_call: Link;
  contact_email: any;
}

export type Block =
  | BannerBlock
  | TrustedByBlock
  | ServiceBlock
  | CaseStudiesBlock
  | CaseStudiesItemGreen
  | InsightsBlock
  | AchievementsBlock
  | AwardsBlock
  | FaqBlock
  | RFPFormBlock;

// Props
interface HomePageProps {
  seo?: any;
  blocks?: any;
  isMobile?: boolean;
}

const HomePage: React.FC<HomePageProps> = ({
  seo,
  blocks,
  isMobile = false,
}) => {
  return (
    <div className="home-page">
      {seo && <HocHead seo={seo} />}

      {blocks?.map((block: any, i: number) => {
        switch (block?.__component) {
          case "blocks.2025-home-banner":
            return (
              <Banner key={i} data={block as BannerBlock} isMobile={isMobile} />
            );

          case "blocks.2025-home-trusted-by":
            return <Trusted key={i} data={block as TrustedByBlock} />;

          case "blocks.2025-home-service":
            return <Service key={i} data={block as ServiceBlock} />;
          case "blocks.2025-home-case-studies":
            return <CaseStudies key={i} data={block as CaseStudiesBlock} />;

          case "blocks.2025-home-insights":
            return <Insights key={i} data={block as InsightsBlock} />;

          case "blocks.2025-home-achievements":
            return <Achievements key={i} data={block as AchievementsBlock} />;

          case "blocks.2025-home-awards":
            return <Awards key={i} data={block as AwardsBlock} />;

          case "blocks.2025-home-fa-qs":
            return <Faq key={i} data={block as FaqBlock} />;

          case "blocks.2025-home-rfp":
            return <RFPForm key={i} data={block as RFPFormBlock} />;

          default:
            return (
              <div
                key={i}
                className="text-red-500 p-4 bg-red-50 border border-red-200 rounded-md mt-4 max-w-[1500px] mx-auto"
              >
                Unknown block type: {block?.__component || "undefined"}
              </div>
            );
        }
      })}
    </div>
  );
};

export default HomePage;
